import { useEffect, useMemo, useState } from 'react';

export const defaultFunnyMessages: string[] = [
  'Give us a sec…',
  'Rustling it up…',
  'Finishing touches…',
  'Brewing something nice…',
  'Wrangling the pixels…',
  'Herding the code…',
  'Adding secret sauce…',
  'Whispering to the server…',
  'Applying a dash of magic…',
  'Convincing the AI…',
  'Just ironing the wrinkles…',
  'Sprinkling glitter…',
  'Bribing the internet…',
  'Warming up the hamster wheel…',
  'Asking nicely…',
  'Applying duct tape…',
  'Performing digital origami…',
  'Tightening virtual screws…',
  'Making it extra shiny…',
  'Painting happy little pixels…',
  'Folding reality… carefully…',
  'Shaking the idea tree…',
  'Doodling in the margins…',
  'Wrapping it in a bow…',
  'Popping it in the oven…',
  'Calibrating nonsense engine…',
  'Running diagnostics… kinda…',
  'Defragmenting the imagination…',
  'Syncing with the mothership…',
  'Debugging the ducks…',
  'Feeding the data squirrels…',
  'Chasing runaway code monkeys…',
  'Teaching penguins to type…',
  'Persuading the bees…',
  'Asking the owls for wisdom…',
  'Whisking the code batter…',
  'Preheating the idea oven…',
  'Adding extra sprinkles…',
  'Taste-testing the pixels…',
  'Pouring the digital coffee…',
  'Letting it simmer…',
  'Baking until golden…',
  'Reading the instructions… maybe…',
  'Making it pretty…',
];

interface UseFunnyMessagesOptions {
  intervalMs?: number;
  initialMessage?: string;
  messages?: string[];
}

/**
 * Returns a rotating message while an async action is active.
 * When inactive, returns the provided initial message (default: "Generating...").
 */
export function useFunnyMessages(
  isActive: boolean,
  options: UseFunnyMessagesOptions = {}
): string {
  const { intervalMs = 3000, initialMessage = 'Generating...', messages = defaultFunnyMessages } = options;

  const shuffledMessages = useMemo(() => {
    const copy = [...messages];
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
    // messages is stable in our usage; if callers pass a new array, this will re-shuffle accordingly
  }, [messages]);

  const [, setIndex] = useState(0);
  const [current, setCurrent] = useState<string>(initialMessage);

  useEffect(() => {
    if (!isActive) {
      setCurrent(initialMessage);
      return;
    }

    // Start with the first shuffled message
    setIndex(0);
    setCurrent(shuffledMessages[0] ?? initialMessage);

    const id = setInterval(() => {
      setIndex((prev) => {
        const next = (prev + 1) % Math.max(1, shuffledMessages.length);
        setCurrent(shuffledMessages[next] ?? initialMessage);
        return next;
      });
    }, intervalMs);

    return () => clearInterval(id);
  }, [isActive, intervalMs, initialMessage, shuffledMessages]);

  return current;
}


